import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { authAPI } from '../../services/api';
import Alert from '../common/Alert';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    const result = await authAPI.resetPassword(token, password);
    setLoading(false);
    if (result.success) {
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } else {
      setError(result.message || 'Could not reset password.');
    }
  };

  return (
    <div className="max-w-md mx-auto py-8">
      <div className="mb-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-700">
          ← Back to sign in
        </Link>
        <h1 className="mt-6 text-3xl font-bold tracking-tight text-ink-900">Reset password</h1>
        <p className="mt-2 text-sm text-ink-500">Choose a new password for your account.</p>
      </div>

      {!token && (
        <Alert type="error" title="Missing token" message="This reset link is invalid. Request a new one from the forgot-password page." className="mb-5" />
      )}
      {error && <Alert type="error" message={error} className="mb-5" />}

      {done ? (
        <Alert type="success" title="Password updated" message="You can now sign in with your new password. Redirecting…" />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-ink-700">New password</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="new-password"
                className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-3.5 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-ink-700">Confirm password</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="new-password"
                className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-3.5 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || !token}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-semibold text-white shadow-soft hover:bg-ink-800 transition disabled:opacity-60"
          >
            {loading ? 'Updating…' : 'Update password'}
            {!loading && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>
      )}
    </div>
  );
};

export default ResetPassword;
